import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import NavigationHeader from '@alltrails/shared/components/NavigationHeader';
import useIpCountryCode from 'hooks/useIpCountryCode';
import RoadblockedAnchor from 'react_components/branch/roadblocked_anchor';
import useUser from 'hooks/useUser';
import { Context } from '@alltrails/shared/types/context';
import { lazy, MouseEvent, Suspense, useState } from 'react';
import { NoSsr } from '@material-ui/core';
import MobileHomeLink from './MobileHomeLink';
import * as styles from './styles/styles.module.scss';
import CustomProvider from '../CustomProvider';

const SignupModal = lazy(() => import('components/SignupModal'));

type NavigationWrapperProps = {
  context: Context;
  currentPage: string;
  locationSlug: string;
};

const Right = ({ context, currentPage, locationSlug }: NavigationWrapperProps): JSX.Element => {
  const user = useUser();

  if (currentPage !== PageStrings.EXPLORE_ALL_PAGE) {
    if (locationSlug) {
      return (
        <div className={styles.rightContainer}>
          <RoadblockedAnchor
            cssId="mobile-explore-link"
            user={user}
            event="trail_header_map"
            href={`/explore/${locationSlug}?mobileMap=true`}
            innerHTML="Map"
            mobileBrowser={context.mobileBrowser}
            languageRegionCode={context.languageRegionCode}
          />
        </div>
      );
    }

    // pathname does not include the language code
    if ((__AT_DATA__.pathname === '/' || __AT_DATA__.pathname === '/?ref=header') && !user?.pro) {
      return (
        <div className={styles.rightContainer}>
          <MobileHomeLink />
        </div>
      );
    }
  }

  return <div className={styles.mobileRightSpacer} />;
};

const Navigation = ({ context, currentPage, locationSlug }: NavigationWrapperProps): JSX.Element => {
  const ipCountryCode = useIpCountryCode();
  const user = useUser();
  const [{ isOpen, redirectUrl }, toggleSignupModal] = useState<{ isOpen: boolean; redirectUrl?: string }>({ isOpen: false });

  return (
    <>
      <NoSsr>
        <Suspense fallback={null}>
          <SignupModal
            isOpen={isOpen}
            onRequestClose={() => {
              toggleSignupModal({ isOpen: false });
            }}
            onSuccess={() => {
              // no-op because of redirectUrl
            }}
            redirectUrl={redirectUrl}
          />
        </Suspense>
      </NoSsr>
      <NavigationHeader
        context={context}
        inUS={ipCountryCode === 'US'}
        rightChildren={<Right context={context} locationSlug={locationSlug} currentPage={currentPage} />}
        user={user}
        toggleSignupModal={(e: MouseEvent<HTMLAnchorElement>, redirectUrl: string) => {
          // !user check isn't necessary because we only call this for logged out props
          e.preventDefault();
          toggleSignupModal({ isOpen: !isOpen, redirectUrl });
        }}
      />
    </>
  );
};

function Wrapper({ context, currentPage, locationSlug }: NavigationWrapperProps) {
  return (
    <CustomProvider>
      <Navigation context={context} currentPage={currentPage} locationSlug={locationSlug} />
    </CustomProvider>
  );
}

export default Wrapper;
